/**
 * Tooltip — informative text shown when the user hovers over, focuses, or long-presses an
 * element. It labels or describes its trigger; it is not a place for essential-only content.
 *
 * Thin re-export of MUI Tooltip. Tooltip has **no LOCAL sub-components** (unlike Card/Dialog it
 * exposes no public parts such as `TooltipHeader`). It is driven entirely through **slots**:
 * - `popper`   → **`Popper`** — SHARED positioning primitive (also used by Autocomplete's popup
 *                and the MUI X pickers popper). Report-only: not re-exported here; customise per
 *                tooltip via `slotProps.popper` (offset/placement/portal), never globally.
 * - `transition` → **`Grow`** (default) — SHARED transition; swap for `Fade`/`Zoom` via `slots`.
 * - `tooltip`  → the label wrapper element (`.MuiTooltip-tooltip`) — an internal styled `<div>`,
 *                not a standalone component; this is where the brand surface/typography lives.
 * - `arrow`    → optional pointer (`.MuiTooltip-arrow`), enabled with `arrow` — internal.
 *
 * IMPORTANT: Tooltip builds on **`Popper`, NOT `Popover`** — so it renders in a portal with **no
 * Modal, no Backdrop and no focus trap** (unlike Menu/Dialog/Select). Nothing else in the design
 * system depends on Tooltip, so there is no ripple risk from styling it.
 *
 * Themed from Figma 2174:11036 (`MuiTooltip` styleOverrides in theme/components.ts): the `tooltip`
 * surface is `surface.inverse` (midnight blue) with `text.white` caption text (`captionSm`),
 * `radius.sm` corners, sp4×sp8 padding and `sizes.maxWidth.xs` (240px) max width; the `arrow` is
 * matched to the surface.
 *
 * MUI's Tooltip has no colour prop, so this thin wrapper adds a typed `color` prop (EXCH-19613):
 * - `inverse` (default) — the midnight-blue surface above.
 * - `white` — `surface.white` with `text.primary` and a `bd1` `intent.neutral.border` outline
 *   (the arrow carries the same outline), for tooltips over dark or busy content.
 * It is mapped to a `data-color` attribute on the `tooltip` slot and styled in the theme via
 * `[data-color]` selectors — the same pattern as `Avatar`. The tooltip renders in a portal, so the
 * attribute must sit on the slot, not on the trigger.
 */
import type { TooltipProps as MuiTooltipProps } from '@mui/material/Tooltip';
import MuiTooltip from '@mui/material/Tooltip';
import { forwardRef } from 'react';

export type TooltipColor = 'inverse' | 'white';

export interface TooltipProps extends Omit<MuiTooltipProps, 'color'> {
  /** Surface colour: `inverse` (midnight blue) · `white` (white with a neutral border). @default 'inverse' */
  color?: TooltipColor;
}

export const Tooltip = forwardRef<HTMLElement, TooltipProps>(function Tooltip(
  { color = 'inverse', slotProps, ...props },
  ref
) {
  const tooltipSlotProps = slotProps?.tooltip;
  // `slotProps.tooltip` may be an object or a function of ownerState — keep the caller's props
  // either way and only add the `data-color` hook the theme keys off.
  const tooltipProps =
    typeof tooltipSlotProps === 'function'
      ? (ownerState: Parameters<typeof tooltipSlotProps>[0]) => ({
          ...tooltipSlotProps(ownerState),
          'data-color': color,
        })
      : { ...tooltipSlotProps, 'data-color': color };
  return <MuiTooltip ref={ref} {...props} slotProps={{ ...slotProps, tooltip: tooltipProps }} />;
});

export { tooltipClasses } from '@mui/material/Tooltip';
